import styled from '@emotion/styled';
import { alpha } from '@mui/system';
import { format, isValid, parseISO } from 'date-fns';
import type { FC } from 'react';

import { theme } from '@/design-system/theme';

import Typography from '../../Typography';
import type { TimeCellProps } from '../types';

export const TIME_CELL_MIN_WIDTH = 176;

const TimeCellRoot = styled('div')<{ $direction?: TimeCellProps['direction'] }>(
  ({ $direction }) => ({
    display: 'flex',
    alignItems: 'center',
    flexDirection: $direction === 'column' ? 'column' : 'row',
    gap: $direction === 'column' ? 0 : 8,
    minWidth: 0,
    height: $direction === 'column' ? 'initial' : 24,
  }),
);

const DateLabel = styled(Typography)({
  flexShrink: 0,
});

const TimeLabel = styled(Typography)({
  minWidth: 0,
});

const Divider = styled('span')(({ theme: dividerTheme }) => ({
  flexShrink: 0,
  width: 1,
  height: 24,
  backgroundColor: dividerTheme.colors.border.default,
}));

function toDate(value: Date | string | number): Date {
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : new Date(value);
}

export const TimeCell: FC<TimeCellProps> = ({ date, direction = 'row' }) => {
  const d = toDate(date);
  const dateStr = format(d, 'yyyy-MM-dd');
  const timeStr = format(d, 'HH:mm');

  return (
    <TimeCellRoot $direction={direction}>
      <DateLabel variant="body2" fontWeight={500} noWrap>
        {dateStr}
      </DateLabel>
      {direction === 'row' && <Divider />}
      <TimeLabel
        variant="body2"
        fontWeight={500}
        color={alpha(theme.colors.white[100], direction === 'column' ? 0.5 : 1)}
        noWrap
      >
        {timeStr}
      </TimeLabel>
    </TimeCellRoot>
  );
};

TimeCell.displayName = 'TimeCell';
